import type { MailCompanyInput, PublicMailCompany } from '@embertime/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Button } from '../components/ui/button'
import { SidePanel } from '../components/ui/side-panel'
import { api } from '../lib/api'
import { notify } from '../lib/notify'

const EMPTY: MailCompanyInput = {
  name: '',
  legalForm: 'gmbh',
  street: '',
  zip: '',
  city: '',
  country: 'DE',
  registerCourt: '',
  registerNumber: '',
  managingDirectors: '',
}
const fields: Array<{ key: keyof MailCompanyInput; label: string }> = [
  { key: 'name', label: 'Firma / Name' },
  { key: 'street', label: 'Straße und Hausnummer' },
  { key: 'zip', label: 'Postleitzahl' },
  { key: 'city', label: 'Sitz / Ort' },
  { key: 'country', label: 'Land (ISO 2)' },
  { key: 'registerCourt', label: 'Registergericht' },
  { key: 'registerNumber', label: 'HRB / HRA' },
  { key: 'managingDirectors', label: 'Geschäftsführer / Inhaber (alle Namen)' },
]

export function MailCompaniesSettings() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['mail-companies'], queryFn: api.listMailCompanies })
  const [editing, setEditing] = useState<PublicMailCompany | 'new' | null>(null)
  const [form, setForm] = useState<MailCompanyInput>(EMPTY)
  const open = (company: PublicMailCompany | 'new') => {
    setEditing(company)
    setForm(
      company === 'new'
        ? EMPTY
        : {
            name: company.name,
            legalForm: company.legalForm,
            street: company.street,
            zip: company.zip,
            city: company.city,
            country: company.country,
            registerCourt: company.registerCourt,
            registerNumber: company.registerNumber,
            managingDirectors: company.managingDirectors,
          },
    )
  }
  const save = useMutation({
    mutationFn: () =>
      editing && editing !== 'new'
        ? api.updateMailCompany(editing.id, form)
        : api.createMailCompany(form),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['mail-companies'] }),
        qc.invalidateQueries({ queryKey: ['mail-identities'] }),
      ])
      setEditing(null)
      notify.success('Firmenprofil gespeichert')
    },
    onError: (error: Error) => notify.error(error.message),
  })
  const remove = useMutation({
    mutationFn: api.deleteMailCompany,
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['mail-companies'] }),
        qc.invalidateQueries({ queryKey: ['mail-identities'] }),
      ])
      setEditing(null)
    },
    onError: (error: Error) => notify.error(error.message),
  })
  return (
    <section
      id="mail-companies"
      className="space-y-3 rounded-2xl border border-border-default bg-bg-elevated p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="font-semibold">Firmenprofile</h2>
          <p className="text-xs text-text-secondary">
            Pflichtangaben für alle Absender einer Firma
          </p>
        </div>
        <Button size="sm" onClick={() => open('new')}>
          Hinzufügen
        </Button>
      </div>
      {data?.companies.map((company) => (
        <button
          key={company.id}
          type="button"
          onClick={() => open(company)}
          className="block w-full rounded-lg border border-border-subtle px-3 py-2 text-left text-sm hover:bg-bg-overlay"
        >
          <span className="font-medium">{company.name}</span>
          <span className="ml-2 text-text-tertiary">{company.legalForm}</span>
        </button>
      ))}
      <SidePanel
        open={!!editing}
        mobileFullHeight
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Firmenprofil anlegen' : 'Firmenprofil bearbeiten'}
        footer={
          <div className="flex w-full gap-2">
            {editing && editing !== 'new' && (
              <Button variant="ghost" onClick={() => remove.mutate(editing.id)}>
                Löschen
              </Button>
            )}
            <Button
              className="ml-auto"
              disabled={save.isPending || !form.name.trim()}
              onClick={() => save.mutate()}
            >
              Speichern
            </Button>
          </div>
        }
      >
        <div className="space-y-3 pb-4">
          <label className="block text-sm">
            Rechtsform
            <select
              className="mt-1 w-full rounded-lg border border-border-default bg-bg-overlay p-2"
              value={form.legalForm}
              onChange={(event) =>
                setForm({ ...form, legalForm: event.target.value as MailCompanyInput['legalForm'] })
              }
            >
              <option value="gmbh">GmbH</option>
              <option value="ug">UG (haftungsbeschränkt)</option>
              <option value="ek">e.K.</option>
              <option value="einzelunternehmen">Einzelunternehmen</option>
            </select>
          </label>
          {fields.map(({ key, label }) => (
            <label key={key} className="block text-sm">
              {label}
              <input
                className="mt-1 w-full rounded-lg border border-border-default bg-bg-overlay p-2"
                value={String(form[key] ?? '')}
                onChange={(event) => setForm({ ...form, [key]: event.target.value })}
              />
            </label>
          ))}
          <p className="text-xs text-text-tertiary">
            Fehlende Angaben werden in der Signatur angezeigt. Sie blockieren das Senden nicht.
          </p>
        </div>
      </SidePanel>
    </section>
  )
}
